/* import __COLOCATED_TEMPLATE__ from './brand-signup-email-password.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, getProperties } from '@ember/object';
import { dropTask, task } from 'ember-concurrency';
import { object, string } from 'yup';
import { validateSchema } from 'sh-www/utils/validators';
import { inject as service } from '@ember/service';

const CLASS_NAME = 'GLM-brand-signup-email-password';

const SCHEMA = object().shape({
  'email': string()
    .required('Please enter a valid email')
    .email('Please enter a valid email'),
  'password': string()
    .required()
    .test(
      'password-requirements',
      'Minimum 8 characters, at least 1 uppercase, 1 lowercase, 1 symbol, and 1 number (0-9) required.',
      function (value) {
        if (!value) return false;
        if (value.length < 8) return false;
        if (value.length >= 14) return true; // Drop the special requirements for passwords >= 14 characters
        if (!/[0-9]/.test(value)) return false;
        if (!/[!"\\#$%&'()*+,-.\/:;<=>?@[\]^_`{|}~]/.test(value)) return false;
        if (!/[a-z]/.test(value)) return false;
        if (!/[A-Z]/.test(value)) return false;
        return true;
      }
    ),
});

class BrandSignupEmailPassword extends Component {
  @service session;

  @tracked email = '';
  @tracked password = '';
  @tracked errors = null;
  @tracked emailEntered = false;

  get className() {
    return CLASS_NAME;
  }

  get submitDisabled() {
    return Boolean(
      this.errors?.email
      || this.errors?.password
      || this.email.length < 1
      || this.password.length < 1
      || this.createUserTask.isRunning
      || this.googleSSOCallback.isRunning
    );
  }

  get validationErrors() {
    const [_, validationErrors = null] = validateSchema(getProperties(this, Object.keys(SCHEMA.fields)), SCHEMA);
    return validationErrors;
  }

  get showPasswordIsValid() {
    return !this.validationErrors?.password;
  }

  @action
  handleInput(field, value) {
    this[field] = value;

    if (this.errors?.[field]) {
      this.errors = { ...this.errors, [field]: undefined };
    }

    if (field === 'email' && !this.emailEntered) {
      try {
        SCHEMA.validateSyncAt('email', this);
        this.emailEntered = true;
      } catch (error) {
        // Do nothing
      }
    }
  }

  @action
  authenticate(e) {
    e.preventDefault();
    this.errors = this.validationErrors;

    if (this.errors) {
      return;
    }

    this.createUserTask.perform(this.email, this.password);
  }

  @task
  *createUserTask(email, password) {
    try {
      yield this.args.createUser.perform(email, password);
    } catch(error) {
      this.errors = {
        email: error.errors?.['email']?.[0],
        password: error.errors?.['password']?.[0],
      };
    }
  }

  @dropTask
  *googleSSOCallback({ credential }) {
    this.errors = {};

    yield this.args.onGoogleSSO(credential);
  }
}

export default BrandSignupEmailPassword;
